import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, MapPin, Clock, DollarSign, ChevronRight, Search } from "lucide-react";
import { Button } from "./ui/button";


import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JOB_TYPE_LABELS = {
  "full-time": "Full-Time",
  "part-time": "Part-Time",
  "contract": "Contract",
  "internship": "Internship"
};

export const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(`${API}/careers`);
        setJobs(response.data);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const departments = [...new Set(jobs.map(j => j.department))].sort();

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = !searchQuery || 
      job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDepartment = !filterDepartment || job.department === filterDepartment;
    return matchesSearch && matchesDepartment;
  });

  return (
    <div className="min-h-screen bg-[#050505]">
      
      

      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-zinc-900 to-[#050505] border-b border-white/5 pt-8">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1600')] bg-cover bg-center opacity-10"></div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-8 bg-white"></div>
            <span className="text-zinc-400 text-sm font-medium uppercase tracking-widest">Antler Sports Network</span>
          </div>
          <h1 className="font-secondary text-5xl md:text-6xl font-black uppercase tracking-tight text-white mb-4">
            Careers
          </h1>
          <p className="text-zinc-400 text-lg max-w-xl">
            Join our team and help bring sports coverage to fans everywhere. We're always looking for passionate individuals.
          </p>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-12">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-20">
            <Briefcase className="w-16 h-16 text-zinc-700 mx-auto mb-6" />
            <h2 className="font-secondary text-2xl font-bold text-white mb-2">No Open Positions</h2>
            <p className="text-zinc-500 max-w-md mx-auto">
              We don't have any open positions at the moment. Check back soon or follow us on social media for updates.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Job List */}
            <div className="lg:col-span-1 space-y-4">
              {/* Filters */}
              <div className="space-y-3 mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search jobs..."
                    className="w-full bg-zinc-900 border border-white/10 text-white pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-white/30"
                  />
                </div>
                <select
                  value={filterDepartment}
                  onChange={(e) => setFilterDepartment(e.target.value)}
                  className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                >
                  <option value="">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>

              <p className="text-zinc-500 text-sm">{filteredJobs.length} open position{filteredJobs.length !== 1 ? 's' : ''}</p>

              {/* Job Cards */}
              {filteredJobs.map((job, index) => (
                <motion.button
                  key={job.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setSelectedJob(job)}
                  className={`w-full text-left p-4 border transition-all ${
                    selectedJob?.id === job.id 
                      ? 'bg-white/10 border-white/20' 
                      : 'bg-zinc-900/50 border-white/5 hover:border-white/10'
                  }`}
                >
                  <h3 className="font-bold text-white mb-1">{job.title}</h3>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400">
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-3 h-3" />
                      {job.department}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {job.location}
                    </span>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Job Details */}
            <div className="lg:col-span-2">
              {selectedJob ? (
                <motion.div
                  key={selectedJob.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-zinc-900/50 border border-white/10 p-6 md:p-8 sticky top-28"
                >
                  <div className="mb-6">
                    <h2 className="font-secondary text-3xl font-bold text-white mb-3">{selectedJob.title}</h2>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        {selectedJob.department}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {selectedJob.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {JOB_TYPE_LABELS[selectedJob.type] || selectedJob.type}
                      </span>
                      {selectedJob.salary_range && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {selectedJob.salary_range}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Description */}
                    <div>
                      <h3 className="font-secondary text-sm font-bold uppercase tracking-widest text-white mb-3">Description</h3>
                      <p className="text-zinc-300 leading-relaxed whitespace-pre-line">{selectedJob.description}</p>
                    </div>

                    {/* Requirements */}
                    {selectedJob.requirements && (
                      <div>
                        <h3 className="font-secondary text-sm font-bold uppercase tracking-widest text-white mb-3">Requirements</h3>
                        <p className="text-zinc-300 leading-relaxed whitespace-pre-line">{selectedJob.requirements}</p>
                      </div>
                    )}

                    {/* Benefits */}
                    {selectedJob.benefits && (
                      <div>
                        <h3 className="font-secondary text-sm font-bold uppercase tracking-widest text-white mb-3">Benefits</h3>
                        <p className="text-zinc-300 leading-relaxed whitespace-pre-line">{selectedJob.benefits}</p>
                      </div>
                    )}

                    {/* Apply Button */}
                    <div className="pt-4 border-t border-white/10">
                      <Button 
                        className="bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-sm px-8 py-3 h-auto rounded-none"
                        onClick={() => window.location.href = `mailto:careers@antlersn.com?subject=Application: ${selectedJob.title}`}
                      >
                        Apply Now
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                      <p className="text-zinc-500 text-xs mt-3">
                        Send your resume and cover letter to careers@antlersn.com
                      </p>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <div className="bg-zinc-900/30 border border-white/5 p-12 text-center h-full flex items-center justify-center">
                  <div>
                    <Briefcase className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-500">Select a job posting to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CareersPage;
